import { SearchOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

interface ClientItem {
  id: string;
  first_name?: string;
  last_name?: string;
  contacts?: { type: string; value: string }[];
}

interface AssignClientDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (client: ClientItem) => void;
  clients: ClientItem[];
  loading?: boolean;
}

const AssignClientDialog: React.FC<AssignClientDialogProps> = ({
  open, onClose, onSelect, clients, loading = false,
}) => {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedId(null);
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return clients;
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      const name = [c.first_name, c.last_name].filter(Boolean).join(' ').toLowerCase();
      const phone = c.contacts?.find((ct) => ct.type === 'phone')?.value || '';
      const email = c.contacts?.find((ct) => ct.type === 'email')?.value || '';
      return name.includes(q) || phone.includes(q) || email.includes(q);
    });
  }, [clients, search]);

  const selectedClient = clients.find((c) => c.id === selectedId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>ربط بعميل موجود</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: '8px !important', minHeight: 300 }}>
        <TextField
          placeholder="بحث بالاسم أو الهاتف أو البريد..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          size="small"
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        {loading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">جارٍ التحميل...</Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">لا يوجد عملاء</Typography>
          </Box>
        ) : (
          <List sx={{ overflow: 'auto', flex: 1, mx: -1 }}>
            {filtered.map((client) => {
              const name = [client.first_name, client.last_name].filter(Boolean).join(' ');
              const phone = client.contacts?.find((ct) => ct.type === 'phone')?.value;
              const email = client.contacts?.find((ct) => ct.type === 'email')?.value;
              const secondary = [phone, email].filter(Boolean).join(' • ');
              return (
                <ListItemButton
                  key={client.id}
                  selected={selectedId === client.id}
                  onClick={() => setSelectedId(client.id)}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemText
                    primary={name || 'بدون اسم'}
                    secondary={secondary || undefined}
                    secondaryTypographyProps={{ dir: 'ltr', sx: { textAlign: 'right' } }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>إلغاء</Button>
        <Button
          variant="contained"
          disabled={!selectedClient}
          onClick={() => selectedClient && onSelect(selectedClient)}
        >
          ربط
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignClientDialog;
